import {requestCanonicalSubdomain, type LocaleStatus} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";

import {parseUrl} from "./parse-url.ts";
import {urlInLanguage} from "./url-in-language.ts";

/**
 * Get the transfer auth URL for a given transfer auth token and continue path query.
 *
 * @param transferAuthToken The transfer auth token to include in the URL.
 * @param continuePath The path to redirect to after authentication.
 * @param continueQuery The query parameters to use with the continue path.
 * @param kaLocale The locale to use for the URL.
 * @param status The status to use for the URL.
 * @returns The transfer auth URL.
 */
export const getTransferAuthUrl = (
    transferAuthToken: string,
    continuePath: string,
    continueQuery: Record<string, string>,
    kaLocale?: string | null,
    status?: LocaleStatus | null,
): string => {
    // Get the language tag that we want to redirect to.
    const languageTag = requestCanonicalSubdomain(kaLocale, kaLocale, status);

    const transferPath = `/transfer_auth?key=${transferAuthToken}&continue=${encodeURIComponent(
        continuePath + queryParamsToSearchString(continueQuery),
    )}`;

    if (RequestInfo.IS_DEV_SERVER && languageTag) {
        const host = `${languageTag}.khanacademy.dev`;
        const transferAuthUrl = `https://${host}${transferPath}`;
        return transferAuthUrl;
    }

    return urlInLanguage(languageTag, parseUrl(transferPath));
};

function queryParamsToSearchString(params: Record<string, string>) {
    let result = new URLSearchParams(params).toString();
    if (result !== "") {
        result = "?" + result;
    }
    return result;
}
